import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { AddressComponent } from './address-component.js';
import { LocalizedString } from './localized-string.js';
import { VersionRange } from '../core/version-range.js';
import { Language } from './language.js';

export class Address extends DataModel {
  static readonly TYPE_ID: string = '841cd6a8ba184e5797f5aa172eed4861';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly country: string | null,
    public readonly administrativeAreaLevel1: AddressComponent | null,
    public readonly administrativeAreaLevel2: AddressComponent | null,
    public readonly administrativeAreaLevel3: AddressComponent | null,
    public readonly administrativeAreaLevel4: AddressComponent | null,
    public readonly administrativeAreaLevel5: AddressComponent | null,
    public readonly locality: AddressComponent | null,
    public readonly sublocalityLevel1: AddressComponent | null,
    public readonly sublocalityLevel2: AddressComponent | null,
    public readonly sublocalityLevel3: AddressComponent | null,
    public readonly sublocalityLevel4: AddressComponent | null,
    public readonly sublocalityLevel5: AddressComponent | null,
    public readonly formattedAddresses: LocalizedString | null,
    public readonly street: string | null,
    public readonly streetNumber: string | null,
    public readonly building: string | null,
    public readonly floor: string | null,
    public readonly room: string | null,
    public readonly postalCode: string | null,
  ) {
    super();
  }

  get $typeId() {
    return Address.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Address.SCHEMA_VERSION);
  }

  get formattedAddress(): string | null {
    return this.formattedAddresses?.getAny() ?? null;
  }

  static create(options: {
    country?: string | null,
    administrativeAreaLevel1?: AddressComponent | null,
    administrativeAreaLevel2?: AddressComponent | null,
    administrativeAreaLevel3?: AddressComponent | null,
    administrativeAreaLevel4?: AddressComponent | null,
    administrativeAreaLevel5?: AddressComponent | null,
    locality?: AddressComponent | null,
    sublocalityLevel1?: AddressComponent | null,
    sublocalityLevel2?: AddressComponent | null,
    sublocalityLevel3?: AddressComponent | null,
    sublocalityLevel4?: AddressComponent | null,
    sublocalityLevel5?: AddressComponent | null,
    formattedAddresses?: LocalizedString | null,
    street?: string | null,
    streetNumber?: string | null,
    building?: string | null,
    floor?: string | null,
    room?: string | null,
    postalCode?: string | null,
  }): Address {
    return new Address(
        options.country ?? null,
        options.administrativeAreaLevel1 ?? null,
        options.administrativeAreaLevel2 ?? null,
        options.administrativeAreaLevel3 ?? null,
        options.administrativeAreaLevel4 ?? null,
        options.administrativeAreaLevel5 ?? null,
        options.locality ?? null,
        options.sublocalityLevel1 ?? null,
        options.sublocalityLevel2 ?? null,
        options.sublocalityLevel3 ?? null,
        options.sublocalityLevel4 ?? null,
        options.sublocalityLevel5 ?? null,
        options.formattedAddresses ?? null,
        options.street ?? null,
        options.streetNumber ?? null,
        options.building ?? null,
        options.floor ?? null,
        options.room ?? null,
        options.postalCode ?? null,
    );
  }

  static ourOffice(): Address {
    return Address.create({
      country: 'RO',
      administrativeAreaLevel1: new AddressComponent(
          'ro_hr',
          LocalizedString.forLanguage(Language.Romanian, 'Harghita'),
      ),
      locality: new AddressComponent(
          'ro_hr_gheorgheni',
          LocalizedString.forLanguage(Language.Romanian, 'Gheorgheni'),
      ),
      postalCode: '535500',
      formattedAddresses: LocalizedString.forLanguage(Language.Romanian, 'Str Pompierilor 56/A'),
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): Address {
    return new Address(
        MapUtils.getOrNull<string>(map, 'country'),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'administrativeAreaLevel1') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'administrativeAreaLevel2') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'administrativeAreaLevel3') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'administrativeAreaLevel4') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'administrativeAreaLevel5') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'locality') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'sublocalityLevel1') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'sublocalityLevel2') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'sublocalityLevel3') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'sublocalityLevel4') as SerializedData),
        context.fromDataOrNull<AddressComponent>(MapUtils.getRaw(map, 'sublocalityLevel5') as SerializedData),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(map, 'formattedAddresses')),
        MapUtils.getOrNull<string>(map, 'street'),
        MapUtils.getOrNull<string>(map, 'streetNumber'),
        MapUtils.getOrNull<string>(map, 'building'),
        MapUtils.getOrNull<string>(map, 'floor'),
        MapUtils.getOrNull<string>(map, 'room'),
        MapUtils.getOrNull<string>(map, 'postalCode'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'country': this.country,
      'administrativeAreaLevel1': context.toDataOrNull(this.administrativeAreaLevel1),
      'administrativeAreaLevel2': context.toDataOrNull(this.administrativeAreaLevel2),
      'administrativeAreaLevel3': context.toDataOrNull(this.administrativeAreaLevel3),
      'administrativeAreaLevel4': context.toDataOrNull(this.administrativeAreaLevel4),
      'administrativeAreaLevel5': context.toDataOrNull(this.administrativeAreaLevel5),
      'locality': context.toDataOrNull(this.locality),
      'sublocalityLevel1': context.toDataOrNull(this.sublocalityLevel1),
      'sublocalityLevel2': context.toDataOrNull(this.sublocalityLevel2),
      'sublocalityLevel3': context.toDataOrNull(this.sublocalityLevel3),
      'sublocalityLevel4': context.toDataOrNull(this.sublocalityLevel4),
      'sublocalityLevel5': context.toDataOrNull(this.sublocalityLevel5),
      'formattedAddresses': context.toDataOrNull(this.formattedAddresses),
      'street': this.street,
      'streetNumber': this.streetNumber,
      'building': this.building,
      'floor': this.floor,
      'room': this.room,
      'postalCode': this.postalCode,
    };
  }

  clone(): this {
    return new Address(
        this.country,
        this.administrativeAreaLevel1?.clone() ?? null,
        this.administrativeAreaLevel2?.clone() ?? null,
        this.administrativeAreaLevel3?.clone() ?? null,
        this.administrativeAreaLevel4?.clone() ?? null,
        this.administrativeAreaLevel5?.clone() ?? null,
        this.locality?.clone() ?? null,
        this.sublocalityLevel1?.clone() ?? null,
        this.sublocalityLevel2?.clone() ?? null,
        this.sublocalityLevel3?.clone() ?? null,
        this.sublocalityLevel4?.clone() ?? null,
        this.sublocalityLevel5?.clone() ?? null,
        this.formattedAddresses?.clone() ?? null,
        this.street,
        this.streetNumber,
        this.building,
        this.floor,
        this.room,
        this.postalCode,
    ) as this;
  }

  isSame(other: Address): boolean {
    if(other === this) {
      return true;
    }

    return other.country === this.country &&
        isSameData(other.administrativeAreaLevel1, this.administrativeAreaLevel1) &&
        isSameData(other.administrativeAreaLevel2, this.administrativeAreaLevel2) &&
        isSameData(other.administrativeAreaLevel3, this.administrativeAreaLevel3) &&
        isSameData(other.administrativeAreaLevel4, this.administrativeAreaLevel4) &&
        isSameData(other.administrativeAreaLevel5, this.administrativeAreaLevel5) &&
        isSameData(other.locality, this.locality) &&
        isSameData(other.sublocalityLevel1, this.sublocalityLevel1) &&
        isSameData(other.sublocalityLevel2, this.sublocalityLevel2) &&
        isSameData(other.sublocalityLevel3, this.sublocalityLevel3) &&
        isSameData(other.sublocalityLevel4, this.sublocalityLevel4) &&
        isSameData(other.sublocalityLevel5, this.sublocalityLevel5) &&
        isSameData(other.formattedAddresses, this.formattedAddresses) &&
        other.street === this.street &&
        other.streetNumber === this.streetNumber &&
        other.building === this.building &&
        other.floor === this.floor &&
        other.room === this.room &&
        other.postalCode === this.postalCode;
  }
}

export class AddressBuilder extends DataModelBuilder<Address> {
  build(context: SerializationContext, map: SerializedData): Address {
    return Address.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Address.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(Address.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In this version we replace the `formattedAddress` field of type `string` with the `formattedAddresses` field of type `LocalizedString`.
    if(version < 2) {
      const formattedAddress = MapUtils.getOrNull<string>(map, 'formattedAddress');
      map['formattedAddresses'] = formattedAddress === null ? null : context.toData(LocalizedString.forLanguage(Language.Hungarian, formattedAddress));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(context: SerializationContext, model: Address, map: SerializedData): SerializedData {
    map['formattedAddress'] = model.formattedAddress;
    map['$schema_version'] = VersionRange.encodedBetween(1, Address.SCHEMA_VERSION);
    return map;
  }
}
